"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CacheService = exports.DEFAULT_OPTIONS = void 0;
const feature_1 = require("../log/feature");
/** The default cache service options. */
exports.DEFAULT_OPTIONS = {};
const ORIGIN = 'CacheService';
/**
 * Manages short-term, or ephemeral data points.
 * @remarks
 * This service is meant to speed up your application, and should only be used when performance
 * can benefit from caching data. Generally, the usage of a cache service adds an unnecessary
 * layer of complexity, so avoid using it unless the application's performance demands it.
 */
class CacheService {
    constructor(options, root) {
        this.root = root;
        this.options = Object.assign(Object.assign({}, exports.DEFAULT_OPTIONS), options);
    }
    _getSpanOpts() {
        return {
            origin: ORIGIN,
            attributes: {
                'cache.store': this.getStore(),
            },
        };
    }
    /**
     * Gets an item from the cache.
     * @param key - the key to get
     * @returns a promise resolving to the value of the key, or null if it's not found.
     */
    get(key) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.get_item', name: key, attributes: Object.assign(Object.assign({}, this._getSpanOpts().attributes), { 'cache.key': key }) }), () => __awaiter(this, void 0, void 0, function* () {
            return this._get(key);
        }));
    }
    put(key, value, secondsOrOpts) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.save', name: key, attributes: Object.assign(Object.assign({}, this._getSpanOpts().attributes), { secondsOrOpts: secondsOrOpts ? JSON.stringify(secondsOrOpts, undefined, 2) : undefined }) }), () => __awaiter(this, void 0, void 0, function* () {
            // We have to do this because of typescript...
            if (typeof secondsOrOpts === 'number') {
                return this._put(key, value, secondsOrOpts);
            }
            else {
                return this._put(key, value, secondsOrOpts);
            }
        }));
    }
    /**
     * Stores a key in the cache, optionally with an expiration. Records the key
     * inside the specified buckets. Use {@link deleteBucket} to delete all of the
     * keys in the bucket later.
     *
     * @param key - the key to store
     * @param value - the value of the key
     * @param seconds - the seconds before the key expires
     * @param buckets - the keys of the buckets to add the key to
     * @returns a boolean indicating whether or not the key was stored
     */
    putWithBuckets(key, value, seconds, buckets) {
        return __awaiter(this, void 0, void 0, function* () {
            const putResult = yield this.put(key, value, seconds);
            if (buckets === null || buckets === void 0 ? void 0 : buckets.length) {
                yield Promise.all(buckets.map((bucket) => __awaiter(this, void 0, void 0, function* () {
                    (0, feature_1.debug)('recording key %s in bucket %s', key, bucket);
                    yield this.setAdd(bucket, [key], seconds);
                })));
            }
            return putResult;
        });
    }
    /**
     * Adds the passed item to a set in the cache.
     * @param key the name of the set
     * @param values the values to add to the set
     * @param seconds the seconds before the entire set expires
     * @returns a promise resolving to the number of items actually added to the set
     */
    setAdd(key, values, seconds) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.save', name: `SetAdd ${key}`, attributes: Object.assign(Object.assign({}, this._getSpanOpts().attributes), { seconds }) }), () => __awaiter(this, void 0, void 0, function* () {
            return this._setAdd(key, values, seconds);
        }));
    }
    /**
     * Removes the passed item from a set in the cache.
     * @param key the name of the set
     * @param values the values to remove from the set
     * @returns a promise resolving to the number of items actually removed from the set
     */
    setRemove(key, values) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.save', name: `SetRemove ${key}` }), () => __awaiter(this, void 0, void 0, function* () {
            return this._setRemove(key, values);
        }));
    }
    /**
     * Gets all members of an existing set.
     * @param key the name of the set
     */
    setMembers(key) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.get_item', name: `SetMembers ${key}`, attributes: Object.assign(Object.assign({}, this._getSpanOpts().attributes), { 'cache.operation': 'SetMembers', 'cache.key': key }) }), () => __awaiter(this, void 0, void 0, function* () {
            return this._setMembers(key);
        }));
    }
    /**
     * Determines if the set has the specified member.
     * @param key the name of the set
     * @param value the value to check for
     */
    setHas(key, value) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.get_item', name: `SetHas ${key}`, attributes: Object.assign(Object.assign({}, this._getSpanOpts().attributes), { 'cache.operation': 'SetHas', 'cache.key': key }) }), () => __awaiter(this, void 0, void 0, function* () {
            return this._setHas(key, value);
        }));
    }
    /**
     * Deletes a key from the cache.
     * @param keys - the keys to delete
     */
    delete(keys) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.delete_item', name: keys.length > 5 ? `[${keys.length} keys]` : keys.join(', ') }), () => __awaiter(this, void 0, void 0, function* () {
            return this._delete(keys);
        }));
    }
    /**
     * Gets all keys inside a bucket, and deletes them (including the bucket).
     * @param buckets - the buckets to delete
     */
    deleteBuckets(buckets) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, feature_1.debug)('deleting keys in buckets %O', buckets);
            const toDelete = new Set();
            yield Promise.all(buckets.map((bucket) => __awaiter(this, void 0, void 0, function* () {
                const keysInBucket = yield this.setMembers(bucket);
                if (keysInBucket === null || keysInBucket === void 0 ? void 0 : keysInBucket.length) {
                    for (const key of keysInBucket) {
                        toDelete.add(key);
                    }
                }
            })));
            yield this.delete([...toDelete, ...buckets]);
        });
    }
    /**
     * Gets multiple keys from the cache in a performant manner.
     * @remarks
     * If you are calling {@link get} for several keys at once, use this method instead.
     * @param keys - the keys to get
     * @returns a promise resolving to the keys and their values
     */
    batchGetKeys(keys) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.get_item', name: keys.length > 5 ? `[${keys.length} keys]` : keys.join(', '), attributes: Object.assign(Object.assign({}, this._getSpanOpts().attributes), { 'cache.operation': 'BatchGet', 'cache.totalKeys': keys.length, 'cache.firstKeys': keys.slice(0, 5) }) }), () => __awaiter(this, void 0, void 0, function* () {
            return this._batchGetKeys(keys);
        }));
    }
    /**
     * Clears the cache.
     */
    clear() {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.clear', name: 'clear cache' }), () => __awaiter(this, void 0, void 0, function* () {
            return this._clear();
        }));
    }
    /**
     * Increments a key by the passed delta. Returns the new value
     * of the key.
     */
    increment(key, delta) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.increment', name: key }), () => __awaiter(this, void 0, void 0, function* () {
            return this._increment(key, delta);
        }));
    }
    /**
     * Expires a key after the specified number of seconds. If a timeout is already
     * configured for the key, it will be updated with this new timeout.
     */
    expire(key, seconds) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.expire', name: key }), () => __awaiter(this, void 0, void 0, function* () {
            return this._expire(key, seconds);
        }));
    }
    /**
     * Gets the remaining TTL for a key. Returns null if there is no TTL on the
     * key or if the key does not exist.
     */
    getSecondsRemaining(key) {
        return this.root.deps.services.error.span(Object.assign(Object.assign({}, this._getSpanOpts()), { op: 'cache.get_seconds_remaining', name: key }), () => __awaiter(this, void 0, void 0, function* () {
            return this._getSecondsRemaining(key);
        }));
    }
    _acquireLock(key, timeout, retryDelay, onLockAcquired) {
        return __awaiter(this, void 0, void 0, function* () {
            const self = this;
            function retry() {
                setTimeout(() => {
                    self._acquireLock(key, timeout, retryDelay, onLockAcquired);
                }, retryDelay);
            }
            try {
                const lockTimeoutValue = Date.now() + timeout + 1;
                const result = yield this.put(key, lockTimeoutValue, {
                    ifNotExists: true,
                    seconds: timeout / 1000,
                });
                if (!result) {
                    throw new Error('Lock failed');
                }
                onLockAcquired(lockTimeoutValue);
            }
            catch (err) {
                retry();
            }
        });
    }
    /**
     * Creates a lock based on the cache service.
     * @param name the name of the lock
     * @param opts options to use when creating the lock
     * @returns A promise, resolving to a callback to call when you are done working with the lock.
     * @remarks From: https://github.com/errorception/redis-lock/blob/master/index.js
     */
    lock(name, opts = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { timeout = 5000, retryDelay = 50 } = opts;
            return new Promise(resolve => {
                const lockName = `lock:${name}`;
                this._acquireLock(lockName, timeout, retryDelay, lockTimeoutValue => {
                    resolve(() => __awaiter(this, void 0, void 0, function* () {
                        if (lockTimeoutValue > Date.now()) {
                            return this.delete([lockName]);
                        }
                    }));
                });
            });
        });
    }
    /**
     * Wraps a callback in a lock to ensure it is only run once at a time.
     * @param name the name of the lock
     * @param callback the code to execute
     * @param opts additional opts to pass to the lock function
     * @returns the return value of callback
     */
    runWithLock(name, callback, _a = {}) {
        var { releasePromise } = _a, opts = __rest(_a, ["releasePromise"]);
        return __awaiter(this, void 0, void 0, function* () {
            const unlock = yield this.lock(name, opts);
            try {
                return yield callback();
            }
            finally {
                if (releasePromise) {
                    releasePromise.finally(unlock);
                }
                else {
                    yield unlock();
                }
            }
        });
    }
}
exports.CacheService = CacheService;
//# sourceMappingURL=CacheService.js.map